import React, { useEffect, useState }          from "react";
import { OKCancel } from "../../lib/iprs-react-library/src/index.js";
import { ChromePicker } from 'react-color';
import IntListInput from "./IntListInputs";

const AppointmentCalendarEditOverLay = ({ state, onCancel, eventDispatch }) => {

    const {
        api,
        appType: {
            appointmentTypeID,
            appointmentTypes,
            Groups
        }
    } = state;

    const appType = appointmentTypes?.find(ap => ap.AppointmentTypeID === appointmentTypeID);

    useEffect(() => {

        const formGrpID     = appType?.GroupID ?? (Groups[0].GroupID);
        const newFormData = { ...appType, GroupID: formGrpID}
        setFormData(newFormData);
    }, [appointmentTypes, appointmentTypeID, Groups, appType]);

    const [formData, setFormData]   = useState(null);

    const save = e => {

        /* API LIMITATION: The backend database supports multiple values for strides, lengths,
         * break times, and day start via these fields:
         *   - Stride (int) + ExtraStrides (varchar, comma-separated)
         *   - DefaultLength (int) + ExtraLengths (varchar, comma-separated)
         *   - BreakEnds (varchar, comma-separated)
         *   - DayStartMins (int)
         *
         * However, the API has critical shortcomings in AppointmentTypeOperation.UpdateAppointmentType
         * and AppointmentType_Update.xml:
         *
         * WHAT THE API READS (from frontend):
         *   - "Stride" (singular) not "Strides" (plural)
         *   - "DefaultLength" (singular) not "Lengths" (plural)
         *   - Does NOT read ExtraStrides, ExtraLengths, BreakEnds, or DayStartMins from request
         *
         * WHAT THE API WRITES (to database):
         *   - Only updates: Stride, DefaultLength, Description, LeadTimeMins, ImageURL, Title,
         *     PublicTitle, Colour, GroupID
         *   - IGNORES: ExtraStrides, ExtraLengths, BreakEnds, DayStartMins
         *   - These fields are READ-ONLY from the frontend perspective
         *
         * WHAT THE API READS (from database to frontend):
         *   - Correctly reads all fields including ExtraStrides, ExtraLengths, BreakEnds, DayStartMins
         *   - Combines them into arrays: Strides[], Lengths[], BreakEndMins[]
         *   - See AppointmentTypeOperation.FromJObject() lines 180-208
         *
         * WORKAROUND: We send both formats to maintain compatibility:
         *   - Stride: First value from Strides array (what API actually uses)
         *   - DefaultLength: First value from Lengths array (what API actually uses)
         *   - Strides/Lengths: Full arrays (ignored by current API, for future enhancement)
         *
         * TODO - Backend fix required in AppointmentTypeOperation.UpdateAppointmentType:
         *   1. Extract Strides and Lengths arrays from JObject values parameter
         *   2. Set Stride = Strides[0], DefaultLength = Lengths[0]
         *   3. Set ExtraStrides = string.Join(",", Strides.Skip(1))
         *   4. Set ExtraLengths = string.Join(",", Lengths.Skip(1))
         *   5. Add BreakEnds and DayStartMins parameters to AppointmentType_Update.xml
         *   6. Update SQL to save ExtraStrides, ExtraLengths, BreakEnds, DayStartMins fields
         */
        const dataToSend = {
            ...formData,
            Stride: formData.Strides?.[0] || 0,         // API expects singular "Stride" field
            DefaultLength: formData.Lengths?.[0] || 30  // API expects singular "DefaultLength" field
        };

        api.transact('AppointmentTypeUpdate', dataToSend)
            .then(r => r.apiResult)
            .then(r => {

                /* values is grouped by group name so unbundle the groups and concatinate */
                const types = Object.values(r.Types).reduce((prev, curr) => [...prev, ...curr], []);
                const appTypeOb = {
                    appType: {
                        appointmentTypes: types,
                        appointmentTypeID: r.AppointmentTypeID,
                        Groups: r.Groups,
                        TypesInGroups: r.Types,
                        overlay: null
                    }
                };

                eventDispatch(appTypeOb);
            })
            .catch(error => {
                console.log('Error: ' + error.message);
                eventDispatch({
                    appType: {
                        overlay: null
                    }
                });
            })
    }

    const handleChange = e => {
        const name = e.target.name;
        const value = e.target.value;
        const newFormData = { ...formData };

        newFormData[name] = value;

        setFormData(newFormData);
    }

    const groupRender = grp => {
        const className = grp.GroupID ? 'selected' : '';
        return <option key={grp.GroupID} value={grp.GroupID} className={className} >{grp.GroupTitle}</option>
    }

    return <section style={{
        position: 'absolute', display: 'block', top: '50%',
        left: '50%', backgroundColor: 'white', zIndex: '100', padding: '2em',
        border: '3px solid var(--colour__primary)',
        transform: 'translate(-50%, -50%)'
    }}>
        {/*<pre>{JSON.stringify(Groups, null, '\t')}</pre>*/}
        <form>
            <label htmlFor='PublicTitle' >Public Title</label>
            <input type='text' name='PublicTitle' value={formData?.PublicTitle}
                onChange={handleChange} size='50' />

            <label htmlFor='Title' >Chronos Title</label>
            <input type='text' name='Title' value={formData?.Title}
                onChange={handleChange} size='50' />

            <label htmlFor='Description' >Public Description</label>
            <input type='text' name='Description' value={formData?.Description}
                onChange={handleChange} size='50' />

            <label htmlFor='DayStartMins' >Day Start (Mins)</label>
            <input type="number" name='DayStartMins' value={formData?.DayStartMins}
                onChange={handleChange} step='5' min='5' max={ 24*60 } size='20'/>

            <label htmlFor='BreakEndMins' >Break Ends (Mins)</label>
            <IntListInput name='BreakEndMins' value={formData?.BreakEndMins}
                onChange={handleChange} />

            <label htmlFor='Lengths' >Length (Mins)</label>
            <IntListInput name='Lengths' value={formData?.Lengths}
                onChange={handleChange} />

            <label htmlFor='Strides' >Stride (Mins)</label>
            <IntListInput name='Strides' value={formData?.Strides}
                onChange={handleChange}  />

            <label htmlFor='LeadTimeMins' >Lead Time (Mins)</label>
            <input type='number' name='LeadTimeMins' value={formData?.LeadTimeMins}
                onChange={handleChange} step='1' min='1' max={30 * 24 * 60} size='20' />

            <label htmlFor='Colour' >Colour</label>
            {/*<input type='text' name='Colour' value={formData?.Colour} size='20'*/}
            {/*    onChange={handleChange} />*/}
            <ColourWrapper value={formData?.Colour} onChange={handleChange} />

            <label htmlFor>Group</label>
            <select value={formData?.GroupID} name='GroupID' onChange={handleChange} >
                { Groups?.map( grp => groupRender(grp) ) }
            </select>

            <OKCancel onOK={save} onCancel={onCancel} />
        </form>
    </section>
};

const ColourWrapper = ({ value, onChange }) => {
    const [showPicker, setShowPicker] = useState(false);

    const handleChange = (colour, c) => {
        onChange({ target: { value: colour.hex, name: 'Colour' } });
    }

    const handleClick = () => {
        setShowPicker(!showPicker);
    }

    const handleClose = () => {
        setShowPicker(false);
    }

    return <div style={{ position: 'relative', display: 'inline-block' }}>
        <div
            onClick={handleClick}
            style={{
                padding: '5px',
                background: '#fff',
                borderRadius: '1px',
                boxShadow: '0 0 0 1px rgba(0,0,0,.1)',
                display: 'inline-block',
                cursor: 'pointer'
            }}
        >
            <div style={{
                width: '36px',
                height: '14px',
                borderRadius: '2px',
                background: value || '#fff'
            }} />
        </div>
        {showPicker && (
            <div style={{ position: 'absolute', zIndex: '200' }}>
                <div
                    style={{
                        position: 'fixed',
                        top: '0px',
                        right: '0px',
                        bottom: '0px',
                        left: '0px'
                    }}
                    onClick={handleClose}
                />
                <div className="color-picker-reset">
                    <ChromePicker color={value} onChange={handleChange} disableAlpha={true} />
                </div>
            </div>
        )}
    </div>

}

export default AppointmentCalendarEditOverLay;